import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPlatformStorage } from '@/util/platformStorage';

type UseInboxFeatureBannerParams = {
  enabled: boolean;
  userId?: string;
  dismissStorageKey: string;
};

export function useInboxFeatureBanner({
  enabled,
  userId,
  dismissStorageKey,
}: UseInboxFeatureBannerParams) {
  const [isVisible, setIsVisible] = useState(false);

  const storage = useMemo(() => getPlatformStorage(userId, 'local'), [userId]);

  const storageKey = useMemo(() => {
    return `${dismissStorageKey}:${userId ?? 'anonymous'}`;
  }, [dismissStorageKey, userId]);

  useEffect(() => {
    if (!enabled) {
      setIsVisible(false);

      return;
    }

    let isMounted = true;

    const loadVisibility = async () => {
      const dismissedAt = await storage.getItem(storageKey);

      if (!isMounted) {
        return;
      }

      setIsVisible(!dismissedAt);
    };

    loadVisibility().catch(() => {
      if (!isMounted) {
        return;
      }

      setIsVisible(true);
    });

    return () => {
      isMounted = false;
    };
  }, [enabled, storage, storageKey]);

  const dismiss = useCallback(async () => {
    setIsVisible(false);

    await storage.setItem(storageKey, new Date().toISOString());
  }, [storage, storageKey]);

  return {
    isVisible,
    dismiss,
  };
}
